import React, { useState, useEffect } from "react";
import { supabase } from "../../supabaseClient";
import FileList from "./FileList";
import FilePreviewModal from "./FilePreviewModal";
import { FiCheck, FiX, FiRefreshCw } from "react-icons/fi";

const SystemStorage = ({ userId, searchQuery = "", filterType = "all" }) => {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filesLoading, setFilesLoading] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [breadcrumb, setBreadcrumb] = useState([]);

  // Fetch all sessions for the user
  useEffect(() => {
    fetchSessions();
  }, [userId]);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("chat_session")
        .select("id, name, created_at, updated_at")
        .eq("user_auth", userId)
        .order("updated_at", { ascending: false });

      if (error) throw error;

      // Filter sessions that have files in storage
      const sessionsWithFiles = await filterSessionsWithFiles(data || []);
      setSessions(sessionsWithFiles);
    } catch (error) {
      console.error("Error fetching sessions:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterSessionsWithFiles = async (sessionList) => {
    const sessionsWithFiles = [];

    for (const session of sessionList) {
      // Check if session folder exists in storage
      const { data, error } = await supabase.storage
        .from("Storage")
        .list(`${userId}/${session.id}`);

      if (!error && data && data.length > 0) {
        sessionsWithFiles.push(session);
      }
    }

    return sessionsWithFiles;
  };

  const fetchFilesForSession = async (sessionId, sessionName) => {
    try {
      setFilesLoading(true);
      setSelectedSession({ id: sessionId, name: sessionName });
      setBreadcrumb([{ id: sessionId, name: sessionName }]);

      const allFiles = [];
      const fileTypes = ["images", "documents", "audios"];

      for (const type of fileTypes) {
        const { data, error } = await supabase.storage
          .from("Storage")
          .list(`${userId}/${sessionId}/${type}`);

        if (!error && data) {
          const filesWithMeta = data
            .filter((file) => file.name !== ".emptyFolderPlaceholder")
            .map((file) => ({
              ...file,
              type: type,
              path: `${userId}/${sessionId}/${type}/${file.name}`,
              fullPath: `${userId}/${sessionId}/${type}/${file.name}`,
            }));
          allFiles.push(...filesWithMeta);
        }
      }

      setFiles(allFiles);
    } catch (error) {
      console.error("Error fetching files:", error);
    } finally {
      setFilesLoading(false);
    }
  };

  const handleBackToSessions = () => {
    setSelectedSession(null);
    setFiles([]);
    setBreadcrumb([]);
  };

  const handleFileClick = (file) => {
    const { data } = supabase.storage
      .from("Storage")
      .getPublicUrl(file.fullPath);

    setPreviewFile({
      url: data.publicUrl,
      name: file.name,
      type: getFileType(file.name, file.type),
    });
    setShowPreview(true);
  };

  const getFileType = (fileName, folderType) => {
    const ext = fileName.split(".").pop().toLowerCase();

    if (
      folderType === "images" ||
      ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"].includes(ext)
    ) {
      return "image";
    }
    if (ext === "pdf") {
      return "pdf";
    }
    if (
      folderType === "audios" ||
      ["mp3", "wav", "ogg", "m4a", "aac"].includes(ext)
    ) {
      return "audio";
    }
    if (["mp4", "webm", "mov", "avi"].includes(ext)) {
      return "video";
    }
    if (["doc", "docx"].includes(ext)) {
      return "word";
    }
    if (["xls", "xlsx"].includes(ext)) {
      return "excel";
    }
    if (["txt", "csv"].includes(ext)) {
      return "text";
    }
    return "other";
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "60px 0",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "16px",
          }}
        >
          <FiRefreshCw
            style={{
              fontSize: "32px",
              color: "var(--teal-bg)",
              animation: "spin 1s linear infinite",
            }}
          />
          <span style={{ opacity: 0.7,color:"var(--text)" }}>Loading sessions...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="system-storage">
      {/* Breadcrumb Navigation */}
      <div className="fm-breadcrumb">
        <span
          className={`fm-breadcrumb-item ${
            !selectedSession ? "active" : "clickable"
          }`}
          onClick={selectedSession ? handleBackToSessions : undefined}
        >
          <i className="bi bi-house-door me-1"></i>
          Sessions
        </span>
        {breadcrumb.map((item, index) => (
          <React.Fragment key={item.id}>
            <i className="bi bi-chevron-right mx-2"></i>
            <span className="fm-breadcrumb-item active">
              <i className="bi bi-folder2 me-1"></i>
              {item.name || `Topic ${item.id.substring(0, 8)}`}
            </span>
          </React.Fragment>
        ))}
      </div>

      {/* Content Area */}
      {!selectedSession ? (
        // Session List View
        <div className="fm-sessions-grid">
          {sessions.length === 0 ? (
            <div className="fm-empty-state">
              <i className="bi bi-folder-x"></i>
              <h5>No Files Found</h5>
              <p>
                You haven't uploaded any files yet. Files uploaded during chat
                sessions will appear here.
              </p>
            </div>
          ) : (
            sessions.map((session) => (
              <div
                key={session.id}
                className="fm-session-card"
                onClick={() => fetchFilesForSession(session.id, session.name)}
              >
                <div className="fm-session-icon">
                  <i className="bi bi-folder-fill"></i>
                </div>
                <div className="fm-session-info">
                  <h6 className="fm-session-name">
                    {session.name || `Topic ${session.id.substring(0, 8)}`}
                  </h6>
                  <span className="fm-session-date">
                    <i className="bi bi-calendar3 me-1"></i>
                    {formatDate(session.updated_at || session.created_at)}
                  </span>
                </div>
                <i className="bi bi-chevron-right fm-session-arrow"></i>
              </div>
            ))
          )}
        </div>
      ) : (
        // Files List View
        <FileList
          files={files}
          loading={filesLoading}
          onFileClick={handleFileClick}
          onBack={handleBackToSessions}
          sessionName={selectedSession.name}
          searchQuery={searchQuery}
          filterType={filterType}
        />
      )}

      {/* File Preview Modal */}
      <FilePreviewModal
        show={showPreview}
        onHide={() => setShowPreview(false)}
        file={previewFile}
      />
    </div>
  );
};

export default SystemStorage;
